import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import Loader from "../../../common/loader/Loader";
import AFSService from "../../../../services/AFSService";
import BookkeepingService from "../../../../services/BookkeepingService";
import WComponent from "../../../common/WComponent";
import RollForwardBar from "./RollForwardBar";

import CurrentYearMovementsService from "../../../../services/CurrentYearMovementsService";
import { formatDate, parseLocalDate } from "./shared/helpers/formatters";
import { NOTE_TEMPLATES, TABS } from "./shared/helpers/constants";

import BSView from "./tabs/balanceSheet/BSView";
import ISView from "./tabs/incomeStatement/ISView";
import TrialBalanceView from "./tabs/trialBalance/TrialBalanceView";
import BookkeepingView from "./tabs/bookkeeping/BookkeepingView";
import CompilationLetterView from "./tabs/preview/CompilationLetterView";
import SummaryView from "./tabs/summary/SummaryView";
import NoteView from "./tabs/notes/NoteView";
import FullPreviewView from "./tabs/preview/FullPreviewView";
import DocumentationView from "./tabs/documentation/DocumentationView";
import AgentModeView from "./tabs/agentMode/AgentModeView";
import ChecklistPanel from "./tabs/compliance/ChecklistPanel";
import LeadSheetView from "./tabs/leadSheets/LeadSheetView";
import YearEndPackageView from "./tabs/package/YearEndPackageView";

import ReviewChecklist from "./tabs/review/ReviewChecklist";

import StatementLineDetailPanel from "./shared/StatementLineDetailPanel";
import AJEModal from "./shared/AJEModal";

import "./afs-walkthrough.css";

function computeChecklistResults({ balanceSheet, incomeStatement, transactions, adjustingEntries, ppeNote, compilationData, entityName, yearEnd }) {
	let totalAssets = balanceSheet?.totalAssets?.current || 0;
	let totalLE = balanceSheet?.totalLiabilitiesAndEquity?.current || 0;
	let bsDifference = Math.abs(totalAssets - totalLE);
	let bsBalances = bsDifference < 1;

	let reOpening = balanceSheet?.retainedEarnings?.opening?.current || 0;
	let reNetIncome = incomeStatement?.netIncome?.current || 0;
	let reClosing = balanceSheet?.retainedEarnings?.closing?.current || 0;
	let reTies = Math.abs((reOpening + reNetIncome) - reClosing) < 1;

	let netIncome = incomeStatement?.netIncome?.current || 0;
	let priorNetIncome = incomeStatement?.netIncome?.prior || 0;
	let totalEquity = balanceSheet?.totalEquity?.current || 0;

	let currentAssetTotal = 0;
	let currentLiabTotal = 0;
	for (let section of (balanceSheet?.sections || [])) {
		if (section.category === "current-assets") currentAssetTotal += (section.totalCurrent || 0);
		if (section.category === "current-liabilities") currentLiabTotal += (section.totalCurrent || 0);
	}
	let workingCapital = currentAssetTotal - currentLiabTotal;

	let goingConcernDetails = [];
	if (workingCapital < 0) goingConcernDetails.push("Negative working capital ($" + Math.abs(Math.round(workingCapital)).toLocaleString("en-CA") + ")");
	if (totalEquity < 0) goingConcernDetails.push("Negative equity ($" + Math.abs(Math.round(totalEquity)).toLocaleString("en-CA") + ")");
	if (netIncome < 0 && priorNetIncome < 0) goingConcernDetails.push("Sustained losses (current and prior year)");

	let totalTransactions = (transactions || []).length;
	let classifiedTransactions = 0;
	for (let tx of (transactions || [])) {
		let cls = tx.classification || "";
		if (cls && cls !== "Unclassified" && cls !== "uncategorized") classifiedTransactions++;
	}
	let unclassifiedCount = totalTransactions - classifiedTransactions;
	let classificationPct = totalTransactions > 0 ? Math.round(classifiedTransactions / totalTransactions * 100) : 100;

	let hasPPE = ppeNote && ppeNote.classes && ppeNote.classes.length > 0;
	let hasDeprecAJE = (adjustingEntries || []).some(function (e) { let d = (e.description || "").toLowerCase(); return d.includes("deprec") || d.includes("amort"); });
	let hasTaxAJE = (adjustingEntries || []).some(function (e) { let d = (e.description || "").toLowerCase(); return d.includes("tax") || d.includes("provision"); });
	let hasTaxSection = (incomeStatement?.sections || []).some(function (s) { return s.category === "tax" && s.totalCurrent !== 0; });
	let compilationNotes = (compilationData && Array.isArray(compilationData.notes)) ? compilationData.notes : [];

	let failures = [];
	let warnings = [];

	if (!bsBalances) failures.push({ id: "perform_bs_balance", label: "Balance sheet does not balance", detail: "Difference of $" + Math.round(bsDifference).toLocaleString("en-CA") + " (Total Assets $" + Math.round(totalAssets).toLocaleString("en-CA") + " vs L&E $" + Math.round(totalLE).toLocaleString("en-CA") + ")", amount: Math.round(bsDifference) });
	if (!reTies) failures.push({ id: "perform_re_ties", label: "Retained earnings do not reconcile", detail: "Opening RE $" + Math.round(reOpening).toLocaleString("en-CA") + " + Net Income $" + Math.round(reNetIncome).toLocaleString("en-CA") + " ≠ Closing RE $" + Math.round(reClosing).toLocaleString("en-CA"), amount: Math.abs(Math.round((reOpening + reNetIncome) - reClosing)) });
	if (goingConcernDetails.length > 0 && !compilationNotes.includes("n_going")) failures.push({ id: "perform_going_concern", label: "Going concern indicators present — no disclosure note", detail: goingConcernDetails.join("; ") });
	if (!entityName || !yearEnd) failures.push({ id: "report_entity", label: "Entity or period not identified on compilation report", detail: "Entity: " + (entityName || "not set") + ", Year end: " + (yearEnd || "not set") });
	if (totalTransactions > 0 && classificationPct < 90) failures.push({ id: "perform_classification", label: "Transactions not fully classified", detail: unclassifiedCount + " unclassified transactions (" + classificationPct + "% complete)" });
	if (hasPPE && !hasDeprecAJE) warnings.push({ id: "perform_depreciation", label: "PP&E present but no depreciation AJE recorded", detail: "Use compute_depreciation to estimate and record amortization" });
	if (netIncome > 0 && !hasTaxAJE && !hasTaxSection) warnings.push({ id: "perform_tax", label: "Net income is positive but no income tax provision", detail: "Net income: $" + Math.round(netIncome).toLocaleString("en-CA") + " — use compute_tax to estimate and record tax AJE" });

	return { failures, warnings, bsDifference: Math.round(bsDifference), bsBalances, reTies, goingConcernDetails, workingCapital: Math.round(workingCapital), totalEquity: Math.round(totalEquity), netIncome: Math.round(netIncome) };
}

class AFSWalkthrough extends WComponent {
	state = {
		activeTab: this.props.compilationData ? "report" : "bs",
		requestedReviewTab: null,
		leadSheetSelectedWpRef: null,
		complianceTabEverOpened: false,
		pendingScrollCheckId: null,
		show2022: false,
		openedLine: null,
		ajeModalOpen: false,
		// One-shot draft that AJEModal prefills its form from when set. Cleared
		// after the modal consumes it (so subsequent open/close cycles don't
		// re-apply the stale prefill).
		ajeModalDraft: null,
		manualChecks: {},
		aiReview: null,
		aiReviewLoading: false,
		noteTexts: this.props.persistedNoteTexts || {},
		customNotes: this.props.persistedCustomNotes || [],
		agentNoteIds: [],
		canScrollLeft: false,
		canScrollRight: false,
	};

	// Bookkeeping refs (were useRef): one-time init guard, AI-review trigger guard.
	noteTextsInitialized = !!this.props.persistedNoteTexts;
	aiReviewTriggered = false;
	// DOM ref for the horizontally-scrolling tab strip.
	tabsRef = React.createRef();
	// Instance handles replacing effect cleanups.
	_scrollRaf = null;
	_tabsScrollEl = null;

	componentDidMount() {
		this.setupTabsScroll();
		this.runScrollToCheck();
		this.maybeRunAiReview();
		this.maybeInitNoteTexts();
	}

	componentDidUpdate(prevProps, prevState) {
		// Effect: scroll to a deep-linked check when pendingScrollCheckId / activeTab change.
		if (prevState.pendingScrollCheckId !== this.state.pendingScrollCheckId || prevState.activeTab !== this.state.activeTab) {
			this.runScrollToCheck();
		}
		// Effect: re-evaluate tab scroll affordances + listeners when the tab set changes.
		if (prevProps.compilationData !== this.props.compilationData || prevState.agentNoteIds !== this.state.agentNoteIds) {
			this.teardownTabsScroll();
			this.setupTabsScroll();
		}
		// Effect: kick off the one-time AI review once statements are available.
		if (prevProps.balanceSheet !== this.props.balanceSheet || prevProps.incomeStatement !== this.props.incomeStatement) {
			this.maybeRunAiReview();
		}
		this.maybeInitNoteTexts();
	}

	componentWillUnmount() {
		if (this._scrollRaf) window.cancelAnimationFrame(this._scrollRaf);
		this.teardownTabsScroll();
	}

	// ── Effect helpers ───────────────────────────────────────────────
	runScrollToCheck = () => {
		if (this._scrollRaf) { window.cancelAnimationFrame(this._scrollRaf); this._scrollRaf = null; }
		if (!this.state.pendingScrollCheckId) return;
		// Wait for the target tab's DOM to mount before scrolling.
		this._scrollRaf = window.requestAnimationFrame(() => {
			let el = document.getElementById("check-" + this.state.pendingScrollCheckId);
			if (el && typeof el.scrollIntoView === "function") {
				el.scrollIntoView({ behavior: "smooth", block: "center" });
				el.classList.add("afs-review__item--highlight");
				setTimeout(function () { el.classList.remove("afs-review__item--highlight"); }, 2000);
			}
			this._scrollRaf = null;
			this.update({ pendingScrollCheckId: null });
		});
	};

	updateTabsScrollState = () => {
		let el = this.tabsRef.current;
		if (!el) return;
		this.update({ canScrollLeft: el.scrollLeft > 1, canScrollRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 });
	};

	setupTabsScroll = () => {
		this.updateTabsScrollState();
		let el = this.tabsRef.current;
		if (!el) return;
		el.addEventListener("scroll", this.updateTabsScrollState, { passive: true });
		window.addEventListener("resize", this.updateTabsScrollState);
		this._tabsScrollEl = el;
	};

	teardownTabsScroll = () => {
		if (this._tabsScrollEl) this._tabsScrollEl.removeEventListener("scroll", this.updateTabsScrollState);
		window.removeEventListener("resize", this.updateTabsScrollState);
		this._tabsScrollEl = null;
	};

	runAiReview = async (balanceSheetData, incomeStatementData) => {
		let { clientId, entityName, yearEnd, retainedEarnings } = this.props;
		this.update({ aiReviewLoading: true });
		try {
			let result = await AFSService.generateReview({
				clientId,
				entityName,
				yearEnd,
				balanceSheet: balanceSheetData,
				incomeStatement: incomeStatementData,
				retainedEarnings,
			});
			if (result && result.narrative) {
				this.update({ aiReview: result.narrative });
			}
		} catch (error) {
			console.log(error);
		}
		this.update({ aiReviewLoading: false });
	};

	maybeRunAiReview = () => {
		if (this.aiReviewTriggered) return;
		if (!this.props.balanceSheet || !this.props.incomeStatement) return;
		this.aiReviewTriggered = true;
		this.runAiReview(this.props.balanceSheet, this.props.incomeStatement);
	};

	// ── Derived data (shared by render + note-text init) ─────────────
	getEnriched = () => {
		let { balanceSheet, incomeStatement, transactions, adjustingEntries, retainedEarnings, ppeNote, gstHstAccount } = this.props;
		if (!balanceSheet) return null;
		return CurrentYearMovementsService.applyCurrentYearMovements({ balanceSheet, incomeStatement, transactions, adjustingEntries, retainedEarnings, ppeNote, gstHstAccount });
	};

	getNoteIds = () => {
		let { compilationData } = this.props;
		let { agentNoteIds, customNotes } = this.state;
		let standardNoteIds = [...new Set([...(compilationData && compilationData.notes ? compilationData.notes : []), ...agentNoteIds])].filter(function (id) { return NOTE_TEMPLATES[id]; });
		let customNoteIds = (customNotes || []).map(function (note) { return note.id; });
		return [...standardNoteIds, ...customNoteIds];
	};

	// Single persist point: hands the parent both the note bodies and the custom-note
	// list together, so a change that touches both (add/remove) saves one consistent snapshot.
	persistNotes = (noteTexts, customNotes) => {
		if (this.props.onNotesChange) this.props.onNotesChange({ noteTexts: noteTexts, customNotes: customNotes });
	};

	// Updates local custom-note list and persists via callback (mirrors setNoteTexts).
	setCustomNotes = (updater) => {
		this.setState((prev) => {
			let nextCustomNotes = typeof updater === "function" ? updater(prev.customNotes) : updater;
			this.persistNotes(prev.noteTexts, nextCustomNotes);
			return { customNotes: nextCustomNotes };
		});
	};

	onAddCustomNote = () => {
		let id = "n_custom_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 7);
		this.setState((prev) => {
			let nextCustomNotes = [...prev.customNotes, { id: id, title: "" }];
			let nextNoteTexts = { ...prev.noteTexts, [id]: "" };
			this.persistNotes(nextNoteTexts, nextCustomNotes);
			return { customNotes: nextCustomNotes, noteTexts: nextNoteTexts };
		});
	};

	onCustomNoteTitleChange = (id, title) => {
		this.setCustomNotes(function (prev) { return prev.map(function (note) { return note.id === id ? { ...note, title: title } : note; }); });
	};

	onRemoveCustomNote = (id) => {
		this.setState((prev) => {
			let nextCustomNotes = prev.customNotes.filter(function (note) { return note.id !== id; });
			let nextNoteTexts = { ...prev.noteTexts };
			delete nextNoteTexts[id];
			this.persistNotes(nextNoteTexts, nextCustomNotes);
			return { customNotes: nextCustomNotes, noteTexts: nextNoteTexts };
		});
	};

	// Wrapper: updates local state and persists via callback (was setNoteTexts).
	setNoteTexts = (updater) => {
		this.setState((prev) => {
			let nextNoteTexts = typeof updater === "function" ? updater(prev.noteTexts) : updater;
			this.persistNotes(nextNoteTexts, prev.customNotes);
			return { noteTexts: nextNoteTexts };
		});
	};

	// Initialize noteTexts once when data is available (moved out of render to
	// avoid a setState-during-render; runs from didMount / didUpdate instead).
	maybeInitNoteTexts = () => {
		if (this.noteTextsInitialized) return;
		let enriched = this.getEnriched();
		if (!enriched) return;
		let bs = enriched.balanceSheet;
		let noteIds = this.getNoteIds();
		if (!(bs && noteIds.length > 0 && Object.keys(this.state.noteTexts).length === 0)) return;

		let { compilationData, entityName, yearEnd } = this.props;
		this.noteTextsInitialized = true;
		let initial = {};
		for (let id of noteIds) {
			let template = NOTE_TEMPLATES[id];
			if (template) {
				initial[id] = template.template(compilationData);
			}
		}
		// Override n_basis with client-specific version that examines the BS
		if (initial.n_basis && bs.sections) {
			let yearEndLabel = yearEnd ? parseLocalDate(yearEnd).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }) : "[Year End]";
			let entity = entityName || "[Entity Name]";
			let intro = "The basis of accounting applied in the preparation of the balance sheet of " + entity + " as at " + yearEndLabel + ", and the statements of retained earnings and income for the year then ended is the historical cost basis and reflects cash transactions with the addition of:";
			let additions = [];
			let sectionNames = new Set();
			for (let s of bs.sections) { if (s.totalPrior !== 0 || s.totalCurrent !== 0) sectionNames.add((s.name || s.label || "").toLowerCase()); }
			if (sectionNames.has("accts. rec., trade & other") || sectionNames.has("accounts receivable")) additions.push("accounts receivable less an allowance for doubtful accounts");
			for (let n of sectionNames) { if (n.includes("investment") && !n.includes("long term")) { additions.push("investments recorded at cost"); break; } }
			if (sectionNames.has("goodwill")) additions.push("goodwill recorded at cost");
			for (let n of sectionNames) { if (n.includes("fixed asset") || n.includes("property") || n.includes("equipment")) { additions.push("property, plant and equipment amortized in accordance with amounts allowable for income tax purposes"); break; } }
			for (let n of sectionNames) { if (n.includes("accounts payable") || n.includes("acc. liab")) { additions.push("accounts payable and accrued liabilities"); break; } }
			for (let n of sectionNames) { if (n.includes("income tax")) { additions.push("current income taxes payable as at the reporting date"); break; } }
			if (additions.length > 0) initial.n_basis = intro + "\n\n" + additions.map(function (a) { return "• " + a; }).join("\n");
		}
		this.setNoteTexts(initial);
	};

	// Callbacks (were useCallback).
	onDrillAccount = (acct) => { this.update({ openedLine: AFSService.describeLineToOpen({ account: acct }) }); };
	onDrillLines = (lines) => { this.update({ openedLine: lines }); };
	onNavigateToLeadSheet = (wpRef) => { this.update({ leadSheetSelectedWpRef: wpRef, activeTab: "leadsheets" }); };
	onDrillClose = () => { this.update({ openedLine: null }); };

	// Deep-link from Compliance tab into another tab (and optionally scroll
	// to a specific check card once the target tab mounts its content).
	onComplianceNavigate = ({ tabId, checkId }) => {
		if (tabId) this.update({ activeTab: tabId });
		if (checkId) this.update({ pendingScrollCheckId: checkId });
		else this.update({ pendingScrollCheckId: null });
	};

	render() {
		let {
			yearEnd,
			loading,
			onClose,
			onDownload,
			onDownloadPDF,
			downloading,
			clientId,
			showComparatives = true,
			comparativeProvenance = null,
			compilationReportSignature = null,
			compilationReport = null,
			periodDescription = null,
			roundedPresentation = null,
			compilationData = null,
			entityName = null,
			clientProfile = null,
			glEntries = null,
			transactions = null,
			adjustingEntries = [],
			chartOfAccounts = [],
			canonicalTaxonomyOptions = [],
			onAdjustingEntriesChange = null,
			onCreateChartOfAccount = null,
			onTransactionClassificationChange = null,
			classifications = [],
			reviewNotes = null,
			crossCheckResults = null,
			crossCheckFacts = null,
			initialAgentResult = null,
			onApproveReclassification = null,
			onApproveAccountMapping = null,
			onApproveContextChange = null,
			onAgentModeComplete = null,
			onSignOff = null,
			signedOff = false,
			clientRequestData = null,
			onAddClientRequest = null,
			onSendClientRequests = null,
			onUpdateClientRequestItem = null,
			onDeleteClientRequestItem = null,
			clientEmail = null,
			onOpenIssues = null,
			onOpenStatementSettings = null,
			onChangeStatementSetting = null,
			statementEntityName = "",
		} = this.props;
		let { activeTab, requestedReviewTab, leadSheetSelectedWpRef, complianceTabEverOpened, show2022, openedLine, ajeModalOpen, ajeModalDraft, manualChecks, aiReview, aiReviewLoading, noteTexts, canScrollLeft, canScrollRight } = this.state;

		if (loading) {
			return (
				<div className="afs-walkthrough afs-walkthrough--loading">
					<Loader />
					<p className="afs-walkthrough__loading-text">Building your financial information…</p>
				</div>
			);
		}

		let { balanceSheet } = this.props;
		if (!balanceSheet) return null;

		let enriched = this.getEnriched();
		let bs = enriched.balanceSheet;
		let is = enriched.incomeStatement;
		let re = enriched.retainedEarnings;
		let ppe = enriched.ppeNote;
		// eslint-disable-next-line no-unused-vars
		let checklistResults = computeChecklistResults({ balanceSheet: bs, incomeStatement: is, transactions, adjustingEntries, ppeNote: ppe, compilationData, entityName, yearEnd });

		let currentYear = yearEnd ? parseLocalDate(yearEnd).getFullYear() : "Current";
		let comparativePeriodEnd = comparativeProvenance && comparativeProvenance.periodEnd;
		let priorYear = comparativePeriodEnd
			? parseLocalDate(comparativePeriodEnd).getFullYear()
			: (yearEnd ? parseLocalDate(yearEnd).getFullYear() - 1 : "Prior");
		let priorPriorYear = typeof priorYear === "number" ? priorYear - 1 : "Prior-2";
		let hasComparativePeriod = comparativeProvenance ? comparativeProvenance.kind !== "none" : true;
		let showsComparativeColumn = showComparatives && hasComparativePeriod;

		let noteIds = this.getNoteIds();

		let reviewDecisions = BookkeepingService.buildReviewDecisions({ transactions: transactions || [] });
		let reviewIssues = BookkeepingService.getReviewIssues({ decisions: reviewDecisions });
		let totalIssueCount = reviewIssues.length;
		let resolvedIssueCount = reviewIssues.filter(issue => issue.status === "resolved").length;
		let issueButtonLabel = totalIssueCount > 0 ? `Issues (${resolvedIssueCount}/${totalIssueCount})` : "Issues";

		let hasTransactions = glEntries != null || (Array.isArray(transactions) && transactions.length > 0);

		let visibleTabs = TABS.filter(function (t) {
			if (t.id === "bookkeeping" && !hasTransactions) return false;
			if (t.id === "report" && !compilationData) return false;
			if (t.id === "preview" && !compilationData) return false;
			if (t.id === "notes" && !compilationData) return false;
			return true;
		});

		let scrollTabs = (direction) => {
			let el = this.tabsRef.current;
			if (!el) return;
			el.scrollBy({ left: direction * Math.max(200, el.clientWidth * 0.6), behavior: "smooth" });
		};

		return (
			<div className="afs-walkthrough">

				{/* Top bar */}
				<div className="afs-walkthrough__top-bar">
					<div className="afs-walkthrough__top-bar-left">
						<button className="afs-walkthrough__btn" onClick={onClose}>
							<Icon.ArrowLeft size={14} />
							Back to Bookkeeping
						</button>
					</div>
					<div className="afs-walkthrough__top-bar-center">
						<p className="afs-walkthrough__top-bar-title">Annual Financial Information</p>
						{yearEnd && <p className="afs-walkthrough__top-bar-sub">Year ended {formatDate(yearEnd)}</p>}
					</div>
					<div className="afs-walkthrough__top-bar-right">
						{clientId && (
							<RollForwardBar
								clientId={clientId}
								showComparativeColumn={show2022}
								onToggleComparativeColumn={() => this.setState(function (v) { return { show2022: !v.show2022 }; })}
								comparativeYearLabel={priorPriorYear}
							/>
						)}
						{this.props.onRefreshFromQuickBooks && this.props.importedTrialBalance && this.props.importedTrialBalance.cameFromQuickBooks && (
							<button
								className="afs-walkthrough__btn"
								data-test="refresh-from-quickbooks"
								onClick={this.props.onRefreshFromQuickBooks}
								title="Read QuickBooks again and see whether anything has changed since these books were pulled"
							>
								<Icon.RefreshCw size={13} />
								Refresh from QuickBooks
							</button>
						)}
						{onOpenStatementSettings && (
							<button
								className="afs-walkthrough__btn"
								data-test="open-statement-settings"
								onClick={onOpenStatementSettings}
								title="How these statements are presented — wording, rounding, signatures, order"
							>
								<Icon.Sliders size={13} />
								Statements
							</button>
						)}
						{onOpenIssues && (
							<button
								className="afs-walkthrough__btn"
								onClick={onOpenIssues}
								title="Raise and track review issues"
							>
								<Icon.AlertCircle size={13} />
								{issueButtonLabel}
							</button>
						)}
						<button
							className={clsx("afs-walkthrough__btn", adjustingEntries.length > 0 && "afs-walkthrough__btn--active")}
							onClick={() => this.update({ ajeModalOpen: true })}
							title="Adjusting Journal Entries"
						>
							<Icon.Edit3 size={13} />
							AJEs{adjustingEntries.length > 0 ? " (" + adjustingEntries.length + ")" : ""}
						</button>
						<button className="afs-walkthrough__btn--download" onClick={onDownload} disabled={downloading}>
							<Icon.Download size={14} />
							{downloading ? "Downloading…" : "Export Excel"}
						</button>
						{onDownloadPDF && (
							<button className="afs-walkthrough__btn--download" onClick={() => onDownloadPDF(noteTexts, noteIds, this.state.customNotes)} disabled={downloading}>
								<Icon.FileText size={14} />
								{downloading ? "Downloading…" : "Export PDF"}
							</button>
						)}
					</div>
				</div>

				{/* Tabs */}
				<div className="afs-walkthrough__tabs-wrap">
					{canScrollLeft && (
						<button className="afs-walkthrough__tabs-scroll afs-walkthrough__tabs-scroll--left" onClick={() => scrollTabs(-1)} aria-label="Scroll tabs left">
							<Icon.ChevronLeft size={18} />
						</button>
					)}
					<div className="afs-walkthrough__tabs" ref={this.tabsRef}>
						{visibleTabs.map((t) => {
							return (
								<button key={t.id} className={clsx("afs-walkthrough__tab", activeTab === t.id && "afs-walkthrough__tab--active")} onClick={() => { this.update({ activeTab: t.id }); if (t.id === "compliance") this.update({ complianceTabEverOpened: true }); }}>
									{t.label}
								</button>
							);
						})}
					</div>
					{canScrollRight && (
						<button className="afs-walkthrough__tabs-scroll afs-walkthrough__tabs-scroll--right" onClick={() => scrollTabs(1)} aria-label="Scroll tabs right">
							<Icon.ChevronRight size={18} />
						</button>
					)}
				</div>

				{/* Review notes banner */}
				{reviewNotes && reviewNotes.length > 0 && (
					<div className="afs-walkthrough__review-notes">
						{reviewNotes.map(function (note, noteIndex) {
							let isOpen = note.status !== "C";
							return (
								<div key={noteIndex} className={clsx("afs-walkthrough__review-note", noteIndex > 0 && "afs-walkthrough__review-note--divided")}>
									<Icon.AlertCircle size={15} color={isOpen ? "#e67e22" : "#81c784"} />
									<span className="afs-walkthrough__review-note-id">{note.id}</span>
									<span className={clsx("afs-walkthrough__review-note-subject", isOpen ? "afs-walkthrough__review-note-subject--open" : "afs-walkthrough__review-note-subject--done")}>{note.subject}</span>
									<span className={clsx("afs-walkthrough__review-note-badge", isOpen ? "afs-walkthrough__review-note-badge--open" : "afs-walkthrough__review-note-badge--done")}>
										{isOpen ? "Open" : "Completed"}
									</span>
								</div>
							);
						})}
					</div>
				)}

				{/* Body */}
				<div className="afs-walkthrough__body-row">
					<div className="afs-walkthrough__body">

						{activeTab === "report" && compilationData && (
							<CompilationLetterView compilationData={compilationData} compilationReportSignature={compilationReportSignature} compilationReport={compilationReport} entityName={entityName} statementEntityName={statementEntityName} onChangeStatementSetting={onChangeStatementSetting} clientProfile={clientProfile} yearEnd={yearEnd} />
						)}

						{activeTab === "tb" && (
							<TrialBalanceView balanceSheet={bs} incomeStatement={is} transactions={transactions} adjustingEntries={adjustingEntries} gstHstAccount={this.props.gstHstAccount} currentYear={currentYear} priorYear={priorYear} onDrillAccount={this.onDrillAccount} />
						)}

						{activeTab === "bs" && (
							<BSView balanceSheet={bs} imbalanceExplanation={AFSService.describeWhatExplainsTheImbalance({ crossCheckResults })} currentYear={currentYear} priorYear={priorYear} priorPriorYear={priorPriorYear} showComparatives={showsComparativeColumn} show2022={show2022} onDrillAccount={this.onDrillAccount} onDrillLines={this.onDrillLines} onNavigateToLeadSheet={this.onNavigateToLeadSheet} onMoveAccount={this.props.onMoveAccount} onSortSection={this.props.onSortSection} onRenameAccount={this.props.onRenameAccount} accountCaptions={this.props.accountCaptions} sectionSortOrder={this.props.sectionSortOrder} />
						)}

						{activeTab === "is" && (
							<ISView incomeStatement={is} currentYear={currentYear} priorYear={priorYear} priorPriorYear={priorPriorYear} showComparatives={showsComparativeColumn} show2022={show2022} onDrillAccount={this.onDrillAccount} onDrillLines={this.onDrillLines} onNavigateToLeadSheet={this.onNavigateToLeadSheet} onMoveAccount={this.props.onMoveAccount} onSortSection={this.props.onSortSection} onRenameAccount={this.props.onRenameAccount} accountCaptions={this.props.accountCaptions} sectionSortOrder={this.props.sectionSortOrder} />
						)}

						{activeTab === "notes" && (
							<div className="afs-walkthrough__notes-list">
								{noteIds.map((id) => {
									let customNote = (this.state.customNotes || []).find(function (note) { return note.id === id; });
									let isCustomNote = !!customNote;
									return (
										<div key={id}>
											{!isCustomNote && (
												<h3 className="afs-walkthrough__note-title">{NOTE_TEMPLATES[id].title}</h3>
											)}
											<NoteView
												noteId={id}
												compilationData={compilationData}
												entityName={entityName}
												yearEnd={yearEnd}
												balanceSheet={bs}
												noteTexts={noteTexts}
												onNoteTextChange={(noteId, text) => this.setNoteTexts(function (prev) { return { ...prev, [noteId]: text }; })}
												isCustom={isCustomNote}
												title={isCustomNote ? customNote.title : undefined}
												onTitleChange={isCustomNote ? (title) => this.onCustomNoteTitleChange(id, title) : undefined}
												onRemove={isCustomNote ? () => this.onRemoveCustomNote(id) : undefined}
											/>
										</div>
									);
								})}
								<button type="button" className="afs-walkthrough__add-note" onClick={this.onAddCustomNote}>
									<Icon.Plus size={16} /> Add note
								</button>
							</div>
						)}

						{activeTab === "summary" && (
							<SummaryView balanceSheet={bs} incomeStatement={is} currentYear={currentYear} priorYear={priorYear} showComparatives={showsComparativeColumn} onDrillAccount={this.onDrillAccount} onDrillLines={this.onDrillLines} />
						)}

						{activeTab === "review" && (
							<ReviewChecklist
								initialReviewTab={requestedReviewTab}
								balanceSheet={bs}
								incomeStatement={is}
								transactions={transactions}
								adjustingEntries={adjustingEntries}
								ppeNote={ppe}
								compilationData={compilationData}
								compilationReportSignature={compilationReportSignature}
								compilationReport={compilationReport}
								onChangeStatementSetting={onChangeStatementSetting}
								importedTrialBalance={this.props.importedTrialBalance}
								manualChecks={manualChecks}
								onManualCheckToggle={(checkId) => {
									this.setState(function (previous) {
										let updated = { ...previous.manualChecks };
										updated[checkId] = !updated[checkId];
										return { manualChecks: updated };
									});
								}}
								aiReview={aiReview}
								aiReviewLoading={aiReviewLoading}
								onGenerateAiReview={() => this.runAiReview(bs, is)}
								onNavigateToTab={(tabId) => this.update({ activeTab: tabId })}
								clientId={clientId}
								entityName={entityName}
								yearEnd={yearEnd}
								province={compilationData?.province || (clientProfile && clientProfile.province)}
								crossCheckResults={crossCheckResults}
								crossCheckFacts={crossCheckFacts}
								chartOfAccounts={chartOfAccounts}
								canonicalTaxonomyOptions={canonicalTaxonomyOptions}
								onCreateChartOfAccount={onCreateChartOfAccount}
								agentProposals={initialAgentResult && initialAgentResult.proposals ? initialAgentResult.proposals : []}
								onApproveContextChange={onApproveContextChange}
								onApproveAccountMapping={onApproveAccountMapping}
								onApproveReclassification={onApproveReclassification}
								onAddNote={(noteId, content) => {
									this.setState(function (prev) { return { agentNoteIds: prev.agentNoteIds.includes(noteId) ? prev.agentNoteIds : [...prev.agentNoteIds, noteId] }; });
									if (content) this.setNoteTexts(function (prev) { return { ...prev, [noteId]: content }; });
								}}
								onApproveAJE={(proposedAJE) => {
									if (onAdjustingEntriesChange) {
										// Proposals come in single-pair shape; convert to multi-line
										// here so every entry in state shares the same canonical shape.
										let lines = Array.isArray(proposedAJE.lines) && proposedAJE.lines.length >= 2
											? proposedAJE.lines
											: [
												{ account: proposedAJE.debitAccount || "", accountDescription: proposedAJE.debitDescription || null, canonicalCode: proposedAJE.debitCanonicalCode || null, side: "DEBIT", amount: proposedAJE.amount },
												{ account: proposedAJE.creditAccount || "", accountDescription: proposedAJE.creditDescription || null, canonicalCode: proposedAJE.creditCanonicalCode || null, side: "CREDIT", amount: proposedAJE.amount },
											];
										let newEntry = {
											description: proposedAJE.description,
											source: "human",
											status: "accepted",
											lines: lines,
											date: yearEnd || new Date().toISOString().split("T")[0],
										};
										onAdjustingEntriesChange([...adjustingEntries, newEntry]);
									}
								}}
								onEditAJEDraft={(draft) => this.update({ ajeModalDraft: draft, ajeModalOpen: true })}
								onSignOff={onSignOff}
								signedOff={signedOff}
								clientRequestData={clientRequestData}
								onAddClientRequest={onAddClientRequest}
								onSendClientRequests={onSendClientRequests}
								onUpdateClientRequestItem={onUpdateClientRequestItem}
								onDeleteClientRequestItem={onDeleteClientRequestItem}
								clientEmail={clientEmail}
							/>
						)}

						{activeTab === "leadsheets" && (
							<LeadSheetView clientId={clientId} selectedWpRef={leadSheetSelectedWpRef} onSelectWpRef={(v) => this.update({ leadSheetSelectedWpRef: v })} onFarChange={onAgentModeComplete} onOpenLine={this.onDrillLines} />
						)}

						{activeTab === "documentation" && (
							<DocumentationView clientId={clientId} />
						)}

						{activeTab === "agent_mode" && (
							<AgentModeView
								clientId={clientId}
								onNavigateToReview={() => this.update({ activeTab: "review" })}
								onNavigateToClientRequests={() => this.update({ requestedReviewTab: "requests", activeTab: "review" })}
								onNavigateToDocumentation={() => this.update({ activeTab: "documentation" })}
								onComplete={onAgentModeComplete}
								transactions={transactions || []}
								chartOfAccounts={chartOfAccounts || []}
								onApproveReclassification={onApproveReclassification}
							/>
						)}

						{complianceTabEverOpened && (
							<div className={clsx("afs-walkthrough__compliance-pane", activeTab === "compliance" && "afs-walkthrough__compliance-pane--active")}>
								<ChecklistPanel clientId={clientId} onNavigate={this.onComplianceNavigate} />
							</div>
						)}

						{activeTab === "bookkeeping" && (
							<BookkeepingView transactions={transactions} classifications={classifications} onClassificationChange={onTransactionClassificationChange} />
						)}

						{activeTab === "preview" && compilationData && (
							<FullPreviewView
								compilationData={compilationData}
								entityName={entityName}
								clientProfile={clientProfile}
								yearEnd={yearEnd}
								balanceSheet={bs}
								incomeStatement={is}
								ppeNote={ppe}
								retainedEarnings={re}
								isFinalized={this.props.isFinalized}
								province={compilationData?.province}
								currentYear={currentYear}
								priorYear={priorYear}
								showComparatives={showsComparativeColumn}
								comparativeProvenance={comparativeProvenance}
								compilationReportSignature={compilationReportSignature}
								compilationReport={compilationReport}
								onChangeStatementSetting={onChangeStatementSetting}
								statementEntityName={statementEntityName}
								periodDescription={periodDescription}
								roundedPresentation={roundedPresentation}
								statementRows={this.props.statementRows}
								approvalBlock={this.props.approvalBlock}
								onDrillAccount={this.onDrillAccount}
								onDrillLines={this.onDrillLines}
								noteTexts={noteTexts}
							noteIds={noteIds}
							customNotes={this.state.customNotes}
							/>
						)}

						{activeTab === "package" && (
							<YearEndPackageView clientId={clientId} wpfEngagementId={this.props.wpfEngagementId} onQuickBooksChanged={this.props.onQuickBooksChanged} />
						)}

					</div>

					{openedLine && (
						<StatementLineDetailPanel
							opened={openedLine}
							clientId={clientId}
							wpfEngagementId={this.props.wpfEngagementId}
							statements={{ balanceSheet: bs, incomeStatement: is }}
							currentYear={currentYear}
							priorYear={priorYear}
							onClose={this.onDrillClose}
						/>
					)}
				</div>

				<AJEModal
					open={ajeModalOpen}
					onClose={() => this.update({ ajeModalOpen: false, ajeModalDraft: null })}
					entries={adjustingEntries}
					onChange={onAdjustingEntriesChange}
					chartOfAccounts={chartOfAccounts}
					canonicalTaxonomyOptions={canonicalTaxonomyOptions}
					onCreateChartOfAccount={onCreateChartOfAccount}
					draft={ajeModalDraft}
					onDraftConsumed={() => this.update({ ajeModalDraft: null })}
				/>

			</div>
		);
	}
}

export default AFSWalkthrough;
